
import { StatCard } from "@/components/dashboard/stat-card"
import { ActivityFeed } from "@/components/dashboard/activity-feed"
import { InvestorPreview } from "@/components/dashboard/investor-preview"
import { EngagementChart } from "@/components/dashboard/engagement-chart"
import { Sparkles } from "lucide-react"
import { IreneLogoMark } from "@/components/irene-logo"
import { useAuth } from "@/lib/auth-context"
import { useMetrics, useActivities, useInvestors } from "@/lib/use-api"
import type { Metric, Investor, Activity } from "@/lib/types"
import type { ApiInvestor, ApiActivity } from "@/lib/api-client"
import { Skeleton } from "@/components/ui/skeleton"
import { AlertCircle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"

function mapApiInvestor(inv: ApiInvestor): Investor {
  const parsedTags: string[] = (() => {
    if (Array.isArray(inv.tags)) return inv.tags
    if (typeof inv.tags === "string") {
      try {
        const parsed = JSON.parse(inv.tags)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return inv.tags ? inv.tags.split(",").map((t: string) => t.trim()) : []
      }
    }
    return []
  })()

  return {
    id: inv.id,
    name: inv.name,
    firm: inv.firm,
    email: inv.email || "",
    stage: (inv.stage as Investor["stage"]) || "Lead",
    type: (inv.type as Investor["type"]) || "data-driven",
    lastContact: inv.updated_at?.split("T")[0] || inv.created_at?.split("T")[0] || "",
    engagementScore: inv.engagement_score || 0,
    investmentThesis: inv.investment_thesis || "",
    notes: [],
    tags: parsedTags,
  }
}

function mapApiActivity(act: ApiActivity): Activity {
  const typeMap: Record<string, Activity["type"]> = {
    meeting: "meeting",
    email: "email",
    call: "email",
    note: "update",
    view: "view",
    deal: "deal",
  }
  return {
    id: act.id,
    type: typeMap[act.type] || "update",
    title: act.title,
    description: act.description || "",
    timestamp: act.created_at,
  }
}

export default function DashboardPage() {
  const { user } = useAuth()
  const { data: metrics, isLoading: metricsLoading, error: metricsError, mutate: mutateMetrics } = useMetrics()
  const { data: apiActivities, isLoading: activitiesLoading, error: activitiesError, mutate: mutateActivities } = useActivities()
  const { data: apiInvestors, isLoading: investorsLoading, error: investorsError, mutate: mutateInvestors } = useInvestors()

  const firstName = user?.name?.split(" ")[0] || "Founder"
  const hasError = metricsError || activitiesError || investorsError

  function retryAll() {
    mutateMetrics()
    mutateActivities()
    mutateInvestors()
  }

  const statMetrics: Metric[] = metrics
    ? [
        {
          label: "Active Investors",
          value: String(metrics.total_investors),
          change: 0,
          changeLabel: "total",
        },
        {
          label: "Avg Engagement",
          value: String(Math.round(metrics.avg_engagement_score || 0)),
          change: 0,
          changeLabel: "score",
        },
        {
          label: "Deal Rooms",
          value: String(metrics.active_deal_rooms),
          change: 0,
          changeLabel: "active",
        },
        {
          label: "Deck Analyses",
          value: String(metrics.total_analyses),
          change: 0,
          changeLabel: "total",
        },
      ]
    : []

  const investors = apiInvestors ? apiInvestors.map(mapApiInvestor) : []
  const activities = apiActivities ? apiActivities.map(mapApiActivity) : []

  const isLoading = metricsLoading || activitiesLoading || investorsLoading

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Good morning, {firstName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {"Here's"} what happened with your investors while you were away.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2">
          <IreneLogoMark size={18} />
          <span className="text-xs font-medium text-primary">Irene is active</span>
        </div>
      </div>

      {/* Error Banner */}
      {hasError && (
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-4">
          <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">
              Unable to load some data
            </p>
            <p className="text-xs text-muted-foreground">
              {(metricsError || activitiesError || investorsError)?.message ||
                "There was a problem connecting to the server. Your data will appear once the connection is restored."}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={retryAll}
            className="shrink-0 gap-1.5 bg-transparent"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {/* AI Insight Banner */}
      {!hasError && investors.length > 0 && (
        <div className="mt-6 flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary/5 via-secondary/10 to-muted p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">AI Insight</p>
            <p className="text-xs text-muted-foreground">
              You have {investors.length} investor{investors.length !== 1 ? "s" : ""} in your pipeline.
              {metrics && metrics.avg_engagement_score > 0 &&
                ` Average engagement score is ${Math.round(metrics.avg_engagement_score)}%.`}
              {" "}Keep your relationships warm with regular updates.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </>
        ) : (
          statMetrics.map((metric) => (
            <StatCard key={metric.label} metric={metric} />
          ))
        )}
      </div>

      {/* Main Content Grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <EngagementChart />
          {investorsLoading ? (
            <Skeleton className="h-64 rounded-xl" />
          ) : investors.length > 0 ? (
            <InvestorPreview investors={investors} />
          ) : (
            <div className="glass-strong rounded-xl p-8 text-center shadow-sm">
              <p className="text-sm text-muted-foreground">
                No investors yet. Add your first investor from the Investors page.
              </p>
            </div>
          )}
        </div>
        <div>
          {activitiesLoading ? (
            <Skeleton className="h-96 rounded-xl" />
          ) : activities.length > 0 ? (
            <ActivityFeed activities={activities} />
          ) : (
            <div className="glass-strong rounded-xl p-8 text-center shadow-sm">
              <p className="text-sm text-muted-foreground">
                No recent activity. Activities will appear as you use Irene.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
